import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { Badge } from '../ui/Page';
import { Person } from '../ui/Avatar';
import { UserSelect, useMembers } from '../ui/UserSelect';
import {
  addRoomMembers,
  listRoomMembers,
  removeRoomMember,
  setRoomMemberRole,
  type RoomMember,
} from './api';

/**
 * The people in a room, and who runs it.
 *
 * The person who created the room is its first admin. An admin adds and removes people and makes
 * other admins; a member sees the list. The last admin cannot be demoted or removed — a room
 * nobody runs is a room nobody can fix.
 */
export function MembersDrawer({
  conversationId,
  onClose,
}: {
  conversationId: string;
  onClose: () => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const { user } = useAuth();
  const { options: people } = useMembers();
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['room-members', conversationId],
    queryFn: () => listRoomMembers(conversationId),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['room-members', conversationId] });
    void client.invalidateQueries({ queryKey: ['chat-conversations'] });
  };

  const add = useMutation({
    mutationFn: (userId: string) => addRoomMembers(conversationId, [userId]),
    onSuccess: () => {
      setAdding(null);
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => removeRoomMember(conversationId, userId),
    onSuccess: refresh,
    onError: (c: unknown) => setError(messageFor(c)),
  });
  const setRole = useMutation({
    mutationFn: (input: { userId: string; role: RoomMember['role'] }) =>
      setRoomMemberRole(conversationId, input.userId, input.role),
    onSuccess: refresh,
    onError: (c: unknown) => setError(messageFor(c)),
  });

  const rows = members.data?.members ?? [];
  const me = rows.find((member) => member.userId === user?.id);
  // A room role, not an RBAC role: who runs this conversation, decided by its own members.
  const runsRoom = (role: RoomMember['role'] | undefined): boolean => role === 'ADMIN';
  const isAdmin = runsRoom(me?.role);
  const admins = rows.filter((member) => runsRoom(member.role)).length;
  const inRoom = new Set(rows.map((member) => member.userId));
  const candidates = people.filter((person) => !inRoom.has(person.id));

  return (
    <aside className="drawer" role="dialog" aria-label={t('chat.members')}>
      <div className="drawer__head">
        <strong>
          {t('chat.members')} <span className="muted">· {rows.length}</span>
        </strong>
        <button type="button" className="button button--quiet button--compact" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>

      <div className="drawer__body stack">
        {isAdmin && (
          <div className="stack__field">
            <span>{t('chat.addMember')}</span>
            <div className="cell-inline">
              <UserSelect
                value={adding}
                options={candidates}
                placeholder={t('chat.pickPerson')}
                onChange={setAdding}
              />
              <button
                type="button"
                className="button button--primary button--compact"
                disabled={!adding || add.isPending}
                onClick={() => adding && add.mutate(adding)}
              >
                {t('chat.add')}
              </button>
            </div>
          </div>
        )}

        {error && (
          <p className="alert small" role="alert">
            {error}
          </p>
        )}

        <ul className="dash__rows">
          {rows.map((member) => {
            const found = people.find((person) => person.id === member.userId);
            const memberRuns = runsRoom(member.role);
            const lastAdmin = memberRuns && admins <= 1;

            return (
              <li key={member.userId}>
                <span className="dash__row">
                  <span className="cell-inline">
                    <Person
                      size="xs"
                      name={found?.name ?? member.name}
                      url={found?.avatarUrl ?? null}
                    />
                    {memberRuns && <Badge tone="info">{t('chat.admin')}</Badge>}
                  </span>
                  {isAdmin && member.userId !== user?.id && (
                    <span className="cell-inline">
                      <button
                        type="button"
                        className="button button--quiet button--compact"
                        disabled={setRole.isPending || lastAdmin}
                        onClick={() =>
                          setRole.mutate({
                            userId: member.userId,
                            role: memberRuns ? 'MEMBER' : 'ADMIN',
                          })
                        }
                      >
                        {memberRuns ? t('chat.demote') : t('chat.promote')}
                      </button>
                      <button
                        type="button"
                        className="button button--quiet button--danger button--compact"
                        disabled={remove.isPending || lastAdmin}
                        onClick={() => remove.mutate(member.userId)}
                      >
                        {t('chat.remove')}
                      </button>
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>

        {!isAdmin && <p className="muted small">{t('chat.adminsOnly')}</p>}
      </div>
    </aside>
  );
}
